import React, { useMemo } from 'react';
import useStyles from '../../theme/useStyles';
import { formatDate, formatCurrency } from '../../utils/format';
import { peakOf, percent, amountOf } from '../../utils/summaryStats';
import {
  KpiCard, KpiGrid, SummaryColumns, TablePanel, BarCell, TotalRow, Badge, SummaryNote
} from '../../components/ui';
import { youthSummary } from './analytics';
import type { FellowshipLike, PersonLike, ProgramLike } from './analytics';

export interface YouthSummaryProps {
  groups: FellowshipLike[];
  /** The church register — fellowship members are whoever fits a rule. */
  people: PersonLike[];
  programs: ProgramLike[];
  loading?: boolean;
  isDarkMode?: boolean;
}

/** The Youth Fellowship module's Summary tab: tiles, then report panels and watch-lists. */
const YouthSummaryView = ({ groups, people, programs, loading, isDarkMode }: YouthSummaryProps) => {
  const t = useStyles(isDarkMode).t;
  const s = useMemo(() => youthSummary(groups, people, programs), [groups, people, programs]);
  const fellowshipPeak = peakOf(s.perFellowship, (r) => r.count);
  const monthPeak = peakOf(s.months, (r) => r.count);
  const typePeak = peakOf(s.byType, (r) => r.total);
  const genderTotal = s.genders.reduce((n, g) => n + g.count, 0);
  const monthCount = s.months.reduce((n, m) => n + m.count, 0);
  const monthBudget = s.months.reduce((n, m) => n + m.total, 0);

  return (
    <>
      <KpiGrid isDarkMode={isDarkMode}>
        <KpiCard label="Fellowships" value={String(s.fellowships)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Youth in Fellowships" value={String(s.youth)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Studying" value={String(s.studying)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Working" value={String(s.working)} color={t.info} isDarkMode={isDarkMode} />
        <KpiCard label="Programs This Year" value={String(s.programsThisYear)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Upcoming (60 d)" value={String(s.upcoming.length)} color={t.primary} isDarkMode={isDarkMode} />
        <KpiCard label="Avg. Attendance" value={s.avgAttendance === null ? '—' : String(s.avgAttendance)} color={t.income} isDarkMode={isDarkMode} />
        <KpiCard label="Budget Used This Year" value={formatCurrency(s.budgetThisYear)} color={t.expense} isDarkMode={isDarkMode} />
        <KpiCard label="Age Changes (90 d)" value={String(s.ageChanges.length)} color={s.ageChanges.length ? t.warning : t.income} isDarkMode={isDarkMode} />
      </KpiGrid>

      <TablePanel
        title="Members per Fellowship"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'name', header: 'Fellowship', render: (r) => <strong>{r.name}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={fellowshipPeak} color={t.primary} isDarkMode={isDarkMode} /> },
          { key: 'male', header: 'Male', align: 'right' },
          { key: 'female', header: 'Female', align: 'right' },
          { key: 'count', header: 'Members', align: 'right' }
        ]}
        rows={s.perFellowship}
        rowKey="name"
        emptyMessage="No fellowship set up yet."
        below={s.perFellowship.length ? (
          <>
            <TotalRow label="Distinct youth (a member may fit more than one fellowship)" value={s.youth} isDarkMode={isDarkMode} />
            <SummaryNote isDarkMode={isDarkMode}>Counted from Members &amp; Families: living members whose age and gender fit each fellowship's rule.</SummaryNote>
          </>
        ) : null}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Age Mix"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Age', nowrap: true, render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.youth)} max={100} showPercent color={t.info} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Youth', align: 'right' }
          ]}
          rows={s.ageBands}
          rowKey="name"
          emptyMessage="No youth in any fellowship yet."
        />
        <TablePanel
          title="Gender Mix"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Gender', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, genderTotal)} max={100} showPercent color={t.primary} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Youth', align: 'right' }
          ]}
          rows={s.genders}
          rowKey="name"
          emptyMessage="No youth in any fellowship yet."
        />
        <TablePanel
          title="Studying vs Working"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Occupation', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: 'Share', render: (r) => <BarCell value={percent(r.count, s.youth)} max={100} showPercent color={t.income} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Youth', align: 'right' }
          ]}
          rows={s.occupations}
          rowKey="name"
          emptyMessage="No youth in any fellowship yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Programs by Month"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="420px"
        columns={[
          { key: 'label', header: 'Month', nowrap: true, render: (r) => <strong>{r.label}</strong> },
          { key: 'bar', header: '', render: (r) => <BarCell value={r.count} max={monthPeak} color={t.info} isDarkMode={isDarkMode} /> },
          { key: 'count', header: 'Programs', align: 'right' },
          { key: 'total', header: 'Budget Used', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
        ]}
        rows={s.months}
        rowKey="month"
        emptyMessage="No programs held yet."
        below={<TotalRow label={`Last 12 months — ${monthCount} programs`} value={formatCurrency(monthBudget)} color={t.expense} isDarkMode={isDarkMode} />}
      />

      <SummaryColumns isDarkMode={isDarkMode}>
        <TablePanel
          title="Programs by Type"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Type', render: (r) => <strong>{r.name}</strong> },
            { key: 'bar', header: '', render: (r) => <BarCell value={r.total} max={typePeak} color={t.expense} isDarkMode={isDarkMode} /> },
            { key: 'count', header: 'Programs', align: 'right' },
            { key: 'total', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
          ]}
          rows={s.byType}
          rowKey="name"
          emptyMessage="No programs recorded yet."
        />
        <TablePanel
          title="Programs by Fellowship"
          isDarkMode={isDarkMode}
          loading={loading}
          columns={[
            { key: 'name', header: 'Fellowship', render: (r) => <strong>{r.name}</strong> },
            { key: 'count', header: 'Programs', align: 'right' },
            { key: 'total', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
          ]}
          rows={s.byFellowship}
          rowKey="name"
          emptyMessage="No programs recorded yet."
        />
      </SummaryColumns>

      <TablePanel
        title="Coming Up — Next 60 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
          { key: 'title', header: 'Program', render: (r) => r.title || '-' },
          { key: 'fellowshipName', header: 'Fellowship', render: (r) => r.fellowshipName || '-' },
          { key: 'progType', header: 'Type', render: (r) => <Badge tone="neutral">{r.progType || '-'}</Badge> },
          { key: 'budget', header: 'Budget', align: 'right', nowrap: true, render: (r) => formatCurrency(amountOf(r.budget)) }
        ]}
        rows={s.upcoming}
        rowKey={(r) => `${r.date}|${r.title}|${r.fellowshipName}`}
        emptyMessage="No programs planned in the next 60 days."
      />

      <TablePanel
        title="Birthdays Changing Fellowship — Next 90 Days"
        isDarkMode={isDarkMode}
        loading={loading}
        minWidth="520px"
        columns={[
          { key: 'name', header: 'Member', render: (r) => <strong>{r.name}</strong> },
          { key: 'fellowship', header: 'Fellowship', render: (r) => r.fellowship },
          { key: 'change', header: 'Change', render: (r) => <Badge tone={r.change === 'Ageing out' ? 'warning' : 'success'}>{r.change}</Badge> },
          { key: 'age', header: 'Turns', align: 'right', render: (r) => r.age + 1 },
          { key: 'birthday', header: 'On', nowrap: true, render: (r) => `${formatDate(r.birthday)} (${r.inDays} d)` }
        ]}
        rows={s.ageChanges}
        rowKey={(r) => `${r.name}|${r.fellowship}|${r.change}`}
        emptyMessage="Nobody moves in or out of a fellowship in the next 90 days."
      />
    </>
  );
};

export default YouthSummaryView;
